/**
 * IT Dashboard UI Components
 * Reusable UI components for the IT Operations Dashboard
 */
import React, { useEffect, useState } from 'react';
import { 
  AlertCircle, 
  CheckCircle, 
  PauseCircle, 
  AlertTriangle,
  FileText,
  Image,
  Video,
  File
} from 'lucide-react';

/* IT-DASH-PREMIUM (Jun 2026): smooth 0 → value count-up (~700ms, ease-out cubic) */
const useCountUp = (value, duration = 700) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (typeof value !== 'number' || Number.isNaN(value)) return undefined;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return display;
};

// KPI Tile Component
//
// Tier system — drives the 3px left-border accent color per the pixel-perfect
// spec. Callers pass the `tier` prop explicitly:
//   - 'critical'   → red left border; flips to green (celebration) when value is 0
//   - 'warning'    → amber left border; flips to green when value is 0
//   - 'unassigned' → purple left border (persistent, regardless of value)
//   - (no tier)    → blue left border (default neutral informational)
//
// Spec mapping:
//   Tasks Overdue       → critical
//   Tasks Due This Week → (neutral)
//   Open IT Requests    → (neutral)
//   On Hold Requests    → (neutral)
//   SLA Breached        → critical
//   Unassigned Tickets  → unassigned
//   SLA At Risk         → warning
export const KPITile = ({ value, label, color, onClick, tier, icon: Icon, delay = 0, testId }) => {
  const numericValue = Number(value) || 0;
  const display = useCountUp(numericValue);
  let tierClass = '';
  if (tier === 'critical') {
    tierClass = numericValue > 0 ? 'kpi-tile--critical' : 'kpi-tile--celebration';
  } else if (tier === 'warning') {
    tierClass = numericValue > 0 ? 'kpi-tile--warning' : 'kpi-tile--celebration';
  } else if (tier === 'unassigned') {
    tierClass = 'kpi-tile--unassigned';
  }

  return (
    <div
      className={`kpi-tile kpi-tile-${color} ${tierClass}`.trim()}
      onClick={onClick}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && onClick) { e.preventDefault(); onClick(); } }}
      role="button"
      tabIndex={0}
      style={{ animationDelay: `${delay}ms` }}
      data-testid={testId}
      aria-label={`${label}: ${numericValue}`}
    >
      {Icon && (
        <span className={`kpi-icon kpi-icon--${color}`} aria-hidden="true">
          <Icon size={15} />
        </span>
      )}
      <span className="kpi-label">{label}</span>
      <span className="kpi-value">{display}</span>
    </div>
  );
};

// Priority Badge Component
export const PriorityBadge = ({ priority }) => {
  const colors = {
    low: 'bg-gray-100 text-gray-700',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    urgent: 'bg-red-100 text-red-800'
  };
  
  return (
    <span className={`priority-badge ${colors[priority] || colors.medium}`}>
      {priority?.charAt(0).toUpperCase() + priority?.slice(1) || 'Medium'}
    </span>
  );
};

// Status Badge Component
export const StatusBadge = ({ status }) => {
  const statusConfig = {
    open: { class: 'status-open', label: 'Open' },
    in_progress: { class: 'status-progress', label: 'In Progress' },
    on_hold: { class: 'status-hold', label: 'On Hold' },
    waiting_on_user: { class: 'status-waiting', label: 'Waiting' },
    resolved: { class: 'status-resolved', label: 'Resolved' },
    closed: { class: 'status-closed', label: 'Closed' },
    reopened: { class: 'status-reopened', label: 'Reopened' }
  };
  
  const config = statusConfig[status] || { class: 'status-open', label: status };
  
  return (
    <span className={`status-badge ${config.class}`}>
      {config.label}
    </span>
  );
};

// SLA Badge Component
export const SLABadge = ({ sla }) => {
  if (!sla) return null;
  
  const statusConfig = {
    breached: { class: 'sla-breached', label: 'SLA Breached', icon: AlertTriangle },
    at_risk: { class: 'sla-at-risk', label: 'At Risk', icon: AlertCircle },
    on_track: { class: 'sla-on-track', label: 'On Track', icon: CheckCircle },
    paused: { class: 'sla-paused', label: 'Paused', icon: PauseCircle },
    met: { class: 'sla-met', label: 'SLA Met', icon: CheckCircle }
  };
  
  const config = statusConfig[sla.overall_status] || statusConfig.on_track;
  const Icon = config.icon;
  
  // Format time remaining
  const formatTime = (hours) => {
    if (hours <= 0) return 'Overdue';
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    if (hours < 24) return `${Math.round(hours)}h`;
    return `${Math.floor(hours / 24)}d ${Math.round(hours % 24)}h`;
  };
  
  const timeRemaining = sla.response?.status !== 'met' && sla.response?.status !== 'breached'
    ? sla.response?.time_remaining_hours
    : sla.resolution?.time_remaining_hours;
  
  return (
    <div className={`sla-badge ${config.class}`} title={`Response: ${sla.response?.status}, Resolution: ${sla.resolution?.status}`}>
      <Icon size={12} />
      <span className="sla-status">{config.label}</span>
      {timeRemaining > 0 && sla.overall_status !== 'met' && (
        <span className="sla-time">{formatTime(timeRemaining)}</span>
      )}
    </div>
  );
};

// Source Badge Component - Shows ticket origin (User Request vs IT Incident)
export const SourceBadge = ({ source, isProactive }) => {
  // Determine source from field or is_proactive flag for backwards compatibility
  const ticketSource = source || (isProactive ? 'it_incident' : 'user_workspace');
  
  const sourceConfig = {
    user_workspace: { 
      class: 'source-user', 
      label: 'User Request',
      icon: '👤'
    },
    it_incident: { 
      class: 'source-it', 
      label: 'IT Incident',
      icon: '🔧'
    }
  };
  
  const config = sourceConfig[ticketSource] || sourceConfig.user_workspace;
  
  return (
    <span className={`source-badge ${config.class}`} title={`Ticket origin: ${config.label}`}>
      <span className="source-icon">{config.icon}</span>
      <span className="source-label">{config.label}</span>
    </span>
  );
};

// Attachment Icon Component - Shows icon based on file type
export const AttachmentIcon = ({ fileType, size = 16 }) => {
  const iconProps = { size, className: 'attachment-type-icon' };
  
  switch (fileType) {
    case 'image':
      return <Image {...iconProps} />;
    case 'video':
      return <Video {...iconProps} />;
    case 'document':
      return <FileText {...iconProps} />;
    default:
      return <File {...iconProps} />;
  }
};

// Format file size
export const formatFileSize = (bytes) => {
  if (!bytes) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Availability Indicator Component
export const AvailabilityIndicator = ({ status }) => {
  const statusColors = {
    available: 'bg-green-500',
    busy: 'bg-yellow-500',
    away: 'bg-gray-400',
    offline: 'bg-red-500'
  };
  
  return (
    <span 
      className={`w-2 h-2 rounded-full inline-block ${statusColors[status] || statusColors.offline}`}
      title={status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown'}
    />
  );
};

// SLA Compliance Card Component
export const SLAComplianceCard = ({ metrics }) => {
  if (!metrics) return null;
  
  const complianceRate = metrics.overall_compliance_rate || 0;
  const complianceClass = complianceRate >= 90 ? 'sla-good' : complianceRate >= 70 ? 'sla-warning' : 'sla-critical';
  
  return (
    <div className={`sla-compliance-card ${complianceClass}`}>
      <div className="sla-compliance-header">
        <span className="sla-compliance-title">SLA Compliance</span>
        <span className="sla-compliance-rate">{complianceRate.toFixed(1)}%</span>
      </div>
      <div className="sla-compliance-details">
        <div className="sla-detail">
          <span className="sla-detail-label">Response</span>
          <span className="sla-detail-value">{metrics.response_compliance_rate?.toFixed(1) || 0}%</span>
        </div>
        <div className="sla-detail">
          <span className="sla-detail-label">Resolution</span>
          <span className="sla-detail-value">{metrics.resolution_compliance_rate?.toFixed(1) || 0}%</span>
        </div>
      </div>
      <div className="sla-compliance-bar">
        <div 
          className="sla-compliance-fill" 
          style={{ width: `${complianceRate}%` }}
        />
      </div>
    </div>
  );
};
